// Internal: the blog on brandwall.app/blog (D-111). Only for Vikba ApS. Every post with its
// status; a post opens in the editor. Claude can do all of this through the admin MCP too.
import type { BlogPostSummary, BlogStatus } from "@brandwall/contracts";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { ApiError, api } from "../api";

interface Posts {
  posts: BlogPostSummary[];
  counts: Record<BlogStatus, number>;
}

export const STATUS_PILL: Record<BlogStatus, { label: string; cls: string }> = {
  draft: { label: "Draft", cls: "" },
  scheduled: { label: "Scheduled", cls: "warn" },
  published: { label: "Live", cls: "ok" },
};

export const when = (iso: string) =>
  new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export function AdminNav({ here }: { here: "blog" | "demos" | "admin" }) {
  return (
    <div className="row wrap" style={{ gap: 12 }}>
      <img src="/logo.svg" alt="Brandwall" style={{ height: 26 }} />
      <span className="spacer" />
      {here !== "admin" && (
        <Link to="/admin" className="small">
          Super admin
        </Link>
      )}
      {here !== "demos" && (
        <Link to="/admin/demos" className="small">
          Demo walls
        </Link>
      )}
      {here !== "blog" && (
        <Link to="/admin/blog" className="small">
          Blog
        </Link>
      )}
      <Link to="/" className="small">
        Back to the app
      </Link>
    </div>
  );
}

export function AdminBlog() {
  const [status, setStatus] = useState<BlogStatus | "all">("all");
  const [q, setQ] = useState("");
  const list = useQuery({
    queryKey: ["blog", status, q],
    queryFn: () =>
      api<Posts>(`/v1/admin/blog/posts?${new URLSearchParams({ status, ...(q ? { q } : {}) })}`),
  });
  if (list.error)
    return (
      <div className="onb">
        {list.error instanceof ApiError && list.error.status === 404
          ? "Page not found"
          : list.error.message}
      </div>
    );
  const c = list.data?.counts;
  return (
    <div className="content" style={{ margin: "0 auto" }}>
      <div className="stack" style={{ gap: 18 }}>
        <AdminNav here="blog" />
        <div className="row wrap">
          <h1 className="spacer">Blog</h1>
          <a
            className="btn secondary sm"
            href="https://brandwall.app/blog"
            target="_blank"
            rel="noreferrer"
          >
            Open the blog
          </a>
          <Link className="btn sm" to="/admin/blog/new">
            New post
          </Link>
        </div>
        <p className="muted">
          Posts on brandwall.app/blog, written in Markdown. Drafts are only seen through their
          preview link; scheduled posts go live by themselves. Claude can write, schedule and
          publish them through the admin MCP.
        </p>
        <div className="row wrap">
          <div className="seg" role="group" aria-label="Show">
            {(["all", "draft", "scheduled", "published"] as const).map((s) => (
              <button
                key={s}
                type="button"
                aria-pressed={status === s}
                onClick={() => setStatus(s)}
              >
                {s === "all" ? "All" : STATUS_PILL[s].label}
                {s !== "all" && c ? ` (${c[s]})` : ""}
              </button>
            ))}
          </div>
          <input
            className="input"
            style={{ maxWidth: 260 }}
            placeholder="Search titles and tags"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <section className="card stack">
          {!list.data && <p className="muted">Loading…</p>}
          {list.data?.posts.length === 0 && <p className="muted">No posts yet.</p>}
          {list.data?.posts.map((p) => (
            <div key={p.id} className="row wrap">
              <span className="spacer">
                <Link to={`/admin/blog/${p.id}`}>
                  <b>{p.title}</b>
                </Link>{" "}
                <span className={`pill ${STATUS_PILL[p.status].cls}`}>
                  {STATUS_PILL[p.status].label}
                </span>
                <span className="small muted" style={{ display: "block" }}>
                  /blog/{p.slug}
                  {p.publishedAt &&
                    ` · ${p.status === "scheduled" ? "goes live" : "live since"} ${when(p.publishedAt)}`}
                  {` · changed ${when(p.updatedAt)}`}
                  {p.tags.length > 0 && ` · ${p.tags.join(", ")}`}
                </span>
              </span>
              {p.status === "published" && (
                <a className="btn secondary sm" href={p.url} target="_blank" rel="noreferrer">
                  View
                </a>
              )}
              <Link className="btn secondary sm" to={`/admin/blog/${p.id}`}>
                Edit
              </Link>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
